import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { api } from "../../lib/api";
import {
  Calendar, Users, ChevronLeft, ChevronRight, Filter,
  Plus, Clock, MapPin, User, CheckCircle, Video,
  Phone, FileText, Coffee, Briefcase, X, Check
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "../../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";

/**
 * Unified Calendar — Care Visits + General Appointments
 * 
 * Supports:
 * - Care visits (client visits with caregiver assignments)
 * - Meetings (internal team meetings)
 * - Calls (phone calls, video calls)
 * - Reminders (personal reminders, follow-ups)
 * - Tasks (deadlines, to-dos with time blocks)
 * 
 * Design Principles:
 * - Familiar calendar experience (like Outlook/Google Calendar)
 * - Filter toggle to show/hide appointment types
 * - Office staff can add any appointment type
 */

// Appointment type definitions with colors and icons
const APPOINTMENT_TYPES = {
  visit: { 
    label: "Care Visit", 
    color: "#3B82F6", 
    icon: MapPin,
    description: "Client care visit"
  },
  meeting: { 
    label: "Meeting", 
    color: "#8B5CF6", 
    icon: Users,
    description: "Team or client meeting"
  },
  call: { 
    label: "Call", 
    color: "#10B981", 
    icon: Phone,
    description: "Phone or video call"
  },
  reminder: { 
    label: "Reminder", 
    color: "#F59E0B", 
    icon: Clock,
    description: "Personal reminder"
  },
  task: { 
    label: "Task", 
    color: "#EC4899", 
    icon: FileText,
    description: "Deadline or to-do"
  },
  blocked: { 
    label: "Blocked Time", 
    color: "#6B7280", 
    icon: Coffee,
    description: "Unavailable time"
  },
};

// Visit status colors
const VISIT_STATUS_COLORS = {
  scheduled: { bg: "#3B82F6", text: "Scheduled" },
  confirmed: { bg: "#10B981", text: "Confirmed" },
  in_progress: { bg: "#8B5CF6", text: "In Progress" },
  completed: { bg: "#6B7280", text: "Completed" },
  cancelled: { bg: "#EF4444", text: "Cancelled" },
  needs_coverage: { bg: "#F59E0B", text: "Needs Coverage" },
};

// Calendar Event Content Component
const CalendarEventContent = ({ arg }) => {
  const type = arg.event.extendedProps.appointment_type || 'visit';
  const TypeIcon = APPOINTMENT_TYPES[type]?.icon || MapPin;
  
  return (
    <div className="p-1 overflow-hidden flex items-start gap-1">
      <TypeIcon className="w-3 h-3 mt-0.5 shrink-0 opacity-80" />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-xs truncate">{arg.event.title}</div>
        {arg.event.extendedProps.caregiver_name && (
          <div className="text-xs opacity-80 truncate">
            {arg.event.extendedProps.caregiver_name}
          </div>
        )}
        {arg.event.extendedProps.location && type !== 'visit' && (
          <div className="text-xs opacity-70 truncate">
            {arg.event.extendedProps.location}
          </div>
        )}
      </div>
    </div>
  );
};

export default function UnifiedCalendar() {
  const navigate = useNavigate();
  const calendarRef = useRef(null);

  // State
  const [events, setEvents] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState("timeGridWeek");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  
  // Type Filters (toggle visibility)
  const [visibleTypes, setVisibleTypes] = useState({
    visit: true,
    meeting: true,
    call: true,
    reminder: true,
    task: true,
    blocked: true,
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Other Filters
  const [filterCaregiver, setFilterCaregiver] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [caregivers, setCaregivers] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Panels & Modals
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createDate, setCreateDate] = useState(null);
  const [createTime, setCreateTime] = useState(null);

  // New Appointment Form State
  const [newAppointment, setNewAppointment] = useState({
    title: "",
    appointment_type: "meeting",
    date: "",
    start_time: "09:00",
    end_time: "10:00",
    location: "",
    description: "",
    attendees: [],
    all_day: false,
  });

  // Load visits from existing API
  const loadVisits = useCallback(async () => {
    if (!dateRange.start || !dateRange.end) return [];
    
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end,
        view: currentView.includes("day") ? "day" : currentView.includes("month") ? "month" : "week",
      });
      
      if (filterCaregiver) params.append("caregiver_id", filterCaregiver);
      if (filterClient) params.append("client_id", filterClient);
      
      const { data } = await api.get(`/scheduling/calendar/events?${params}`);
      return data.events.map(e => ({ ...e, appointment_type: 'visit' }));
    } catch (err) {
      console.error("Failed to load visits:", err);
      return [];
    }
  }, [dateRange, currentView, filterCaregiver, filterClient]);

  // Load general appointments
  const loadAppointments = useCallback(async () => {
    if (!dateRange.start || !dateRange.end) return [];
    
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      
      const { data } = await api.get(`/appointments?${params}`);
      return data.appointments || [];
    } catch (err) {
      // Appointments endpoint may not exist yet - that's okay
      console.log("Appointments API not available yet");
      return [];
    }
  }, [dateRange]);

  // Combined load function
  const loadAllEvents = useCallback(async () => {
    setLoading(true);
    try {
      const [visits, appts] = await Promise.all([
        loadVisits(),
        loadAppointments(),
      ]);
      
      // Combine and format events
      const allEvents = [
        ...visits,
        ...appts.map(a => ({
          id: `appt-${a.id}`,
          title: a.title,
          start: a.all_day ? a.date : `${a.date}T${a.start_time}`,
          end: a.all_day ? a.date : `${a.date}T${a.end_time}`,
          allDay: a.all_day,
          backgroundColor: APPOINTMENT_TYPES[a.appointment_type]?.color || '#6B7280',
          borderColor: APPOINTMENT_TYPES[a.appointment_type]?.color || '#6B7280',
          extendedProps: {
            ...a,
            appointment_type: a.appointment_type,
          }
        }))
      ];
      
      setEvents(allEvents);
    } catch (err) {
      console.error("Failed to load events:", err);
    } finally {
      setLoading(false);
    }
  }, [loadVisits, loadAppointments]);

  // Load filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [caregiversRes, clientsRes] = await Promise.all([
          api.get("/scheduling/calendar/resources?resource_type=caregivers"),
          api.get("/scheduling/calendar/resources?resource_type=clients"),
        ]);
        setCaregivers(caregiversRes.data.resources || []);
        setClients(clientsRes.data.resources || []);
      } catch (err) {
        console.error("Failed to load filters:", err);
      }
    };
    loadFilters();
  }, []);

  useEffect(() => {
    loadAllEvents();
  }, [loadAllEvents]);

  // Filter events by visible types
  const filteredEvents = events.filter(e => {
    const type = e.extendedProps?.appointment_type || e.appointment_type || 'visit';
    return visibleTypes[type];
  });

  // Calendar handlers
  const handleDatesSet = (dateInfo) => {
    setDateRange({
      start: dateInfo.startStr.split("T")[0],
      end: dateInfo.endStr.split("T")[0],
    });
  };

  const handleEventClick = (info) => {
    const props = info.event.extendedProps;
    setSelectedEvent({ 
      ...props, 
      id: info.event.id, 
      title: info.event.title,
      start: info.event.startStr,
      end: info.event.endStr,
    });
  };

  const handleDateSelect = (selectInfo) => {
    const date = selectInfo.startStr.split("T")[0];
    const time = selectInfo.startStr.split("T")[1]?.substring(0, 5) || "09:00";
    const endTime = selectInfo.endStr.split("T")[1]?.substring(0, 5) || "10:00";
    
    setCreateDate(date);
    setCreateTime(time);
    setNewAppointment(prev => ({
      ...prev,
      date,
      start_time: time,
      end_time: endTime,
      all_day: selectInfo.allDay,
    }));
    setShowCreateModal(true);
  };

  const handleEventDrop = async (info) => {
    const event = info.event;
    const props = event.extendedProps;
    
    // Only handle visits for now (appointments will need separate API)
    if (props.appointment_type === 'visit' && props.visit_id) {
      const newDate = event.startStr.split("T")[0];
      const newStartTime = event.startStr.split("T")[1]?.substring(0, 5);
      const newEndTime = event.endStr.split("T")[1]?.substring(0, 5);
      
      try {
        await api.put(`/scheduling/visits/${props.visit_id}`, {
          visit_date: newDate,
          start_time: newStartTime,
          end_time: newEndTime,
        });
        loadAllEvents();
      } catch (err) {
        console.error("Failed to update:", err);
        info.revert();
      }
    }
  };

  // Create new appointment
  const handleCreateAppointment = async () => {
    if (newAppointment.appointment_type === 'visit') {
      // Redirect to visit creation flow
      navigate(`/operations/scheduling/new?date=${newAppointment.date}`);
      setShowCreateModal(false);
      return;
    }

    try {
      await api.post('/appointments', newAppointment);
      setShowCreateModal(false);
      setNewAppointment({
        title: "",
        appointment_type: "meeting",
        date: "",
        start_time: "09:00",
        end_time: "10:00",
        location: "",
        description: "",
        attendees: [],
        all_day: false,
      });
      loadAllEvents();
    } catch (err) {
      console.error("Failed to create appointment:", err);
      // For now, just close modal (API may not exist yet)
      setShowCreateModal(false);
    }
  };

  const toggleTypeVisibility = (type) => {
    setVisibleTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const calendarApi = calendarRef.current?.getApi();

  return (
    <div className="h-screen flex flex-col bg-[#FBFBFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#176DFF]" />
              <h1 className="text-xl font-semibold text-[#0D1B33]">Calendar</h1>
            </div>
            
            {/* Navigation */}
            <div className="flex items-center gap-1 ml-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => calendarApi?.prev()}
                className="border-[#E2E8F0]"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => calendarApi?.today()}
                className="border-[#E2E8F0]"
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => calendarApi?.next()}
                className="border-[#E2E8F0]"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* View Switcher */}
            <div className="flex items-center gap-1 ml-4 bg-[#F1F5F9] rounded-lg p-1">
              {[
                { key: "timeGridDay", label: "Day" },
                { key: "timeGridWeek", label: "Week" },
                { key: "dayGridMonth", label: "Month" },
                { key: "listWeek", label: "List" },
              ].map((view) => (
                <Button
                  key={view.key}
                  variant={currentView === view.key ? "default" : "ghost"}
                  size="sm"
                  onClick={() => {
                    setCurrentView(view.key);
                    calendarApi?.changeView(view.key);
                  }}
                  className={currentView === view.key ? "bg-white shadow-sm" : "hover:bg-white/50"}
                >
                  {view.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Type Filter Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={`border-[#E2E8F0] ${showFilters ? 'bg-[#F1F5F9]' : ''}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>

            {/* Create New */}
            <Button
              onClick={() => {
                setCreateDate(new Date().toISOString().split('T')[0]);
                setNewAppointment(prev => ({
                  ...prev,
                  date: new Date().toISOString().split('T')[0],
                }));
                setShowCreateModal(true);
              }}
              className="bg-[#176DFF] hover:bg-[#176DFF]/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              New
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
            <div className="flex items-center gap-6">
              <span className="text-sm font-medium text-[#64748B]">Show:</span>
              {Object.entries(APPOINTMENT_TYPES).map(([key, type]) => {
                const Icon = type.icon;
                return (
                  <button
                    key={key}
                    onClick={() => toggleTypeVisibility(key)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
                      visibleTypes[key] 
                        ? 'bg-opacity-100' 
                        : 'bg-opacity-20 opacity-50'
                    }`}
                    style={{ 
                      backgroundColor: visibleTypes[key] ? `${type.color}20` : '#F1F5F9',
                      color: visibleTypes[key] ? type.color : '#64748B',
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {type.label}
                    {visibleTypes[key] && <Check className="w-3 h-3 ml-1" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* Calendar */}
      <div className="flex-1 p-6 overflow-hidden">
        <div className="bg-white rounded-xl border border-[#E2E8F0] h-full p-4">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView={currentView}
            headerToolbar={false}
            events={filteredEvents}
            eventContent={CalendarEventContent}
            datesSet={handleDatesSet}
            eventClick={handleEventClick}
            select={handleDateSelect}
            eventDrop={handleEventDrop}
            selectable={true}
            editable={true}
            droppable={true}
            dayMaxEvents={3}
            height="100%"
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            allDaySlot={true}
            nowIndicator={true}
            slotDuration="00:30:00"
            eventTimeFormat={{
              hour: "numeric",
              minute: "2-digit",
              meridiem: "short",
            }}
          />
        </div>
      </div>

      {/* Create Appointment Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>New Appointment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Appointment Type */}
            <div className="space-y-2">
              <Label>Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(APPOINTMENT_TYPES).map(([key, type]) => {
                  const Icon = type.icon;
                  const isSelected = newAppointment.appointment_type === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setNewAppointment(prev => ({ ...prev, appointment_type: key }))}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? 'border-[#176DFF] bg-[#176DFF]/5' 
                          : 'border-[#E2E8F0] hover:border-[#CBD5E1]'
                      }`}
                    >
                      <Icon className="w-5 h-5" style={{ color: isSelected ? type.color : '#64748B' }} />
                      <span className={`text-xs font-medium ${isSelected ? 'text-[#0D1B33]' : 'text-[#64748B]'}`}>
                        {type.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder={`Enter ${APPOINTMENT_TYPES[newAppointment.appointment_type]?.label.toLowerCase()} title...`}
                value={newAppointment.title}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={newAppointment.date}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>All Day</Label>
                  <Switch
                    checked={newAppointment.all_day}
                    onCheckedChange={(checked) => setNewAppointment(prev => ({ ...prev, all_day: checked }))}
                  />
                </div>
              </div>
            </div>

            {!newAppointment.all_day && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_time">Start Time</Label>
                  <Input
                    id="start_time"
                    type="time"
                    value={newAppointment.start_time}
                    onChange={(e) => setNewAppointment(prev => ({ ...prev, start_time: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_time">End Time</Label>
                  <Input
                    id="end_time"
                    type="time"
                    value={newAppointment.end_time}
                    onChange={(e) => setNewAppointment(prev => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>
            )}

            {/* Location (for meetings/calls) */}
            {['meeting', 'call'].includes(newAppointment.appointment_type) && (
              <div className="space-y-2">
                <Label htmlFor="location">
                  {newAppointment.appointment_type === 'call' ? 'Call Link / Number' : 'Location'}
                </Label>
                <Input
                  id="location"
                  placeholder={newAppointment.appointment_type === 'call' ? 'Zoom link or phone number' : 'Conference room, address, etc.'}
                  value={newAppointment.location}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, location: e.target.value }))}
                />
              </div>
            )}

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Notes (optional)</Label>
              <Textarea
                id="description"
                placeholder="Add any additional details..."
                value={newAppointment.description}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAppointment}
              disabled={!newAppointment.title || !newAppointment.date}
              className="bg-[#176DFF] hover:bg-[#176DFF]/90"
            >
              {newAppointment.appointment_type === 'visit' ? 'Continue to Visit Creation' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Detail Sheet */}
      <Sheet open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {selectedEvent && (
                <>
                  {(() => {
                    const type = selectedEvent.appointment_type || 'visit';
                    const Icon = APPOINTMENT_TYPES[type]?.icon || MapPin;
                    return <Icon className="w-5 h-5" style={{ color: APPOINTMENT_TYPES[type]?.color }} />;
                  })()}
                  {selectedEvent.title}
                </>
              )}
            </SheetTitle>
          </SheetHeader>
          
          {selectedEvent && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <Clock className="w-4 h-4 text-[#64748B]" />
                <span>
                  {new Date(selectedEvent.start).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
              
              {!selectedEvent.all_day && (
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="w-4 h-4 text-[#64748B]" />
                  <span>
                    {new Date(selectedEvent.start).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    })}
                    {' - '}
                    {new Date(selectedEvent.end).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
              )}

              {selectedEvent.location && (
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-[#64748B]" />
                  <span>{selectedEvent.location}</span>
                </div>
              )}

              {selectedEvent.caregiver_name && (
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-[#64748B]" />
                  <span>{selectedEvent.caregiver_name}</span>
                </div>
              )}

              {selectedEvent.client_name && (
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-[#64748B]" />
                  <span>{selectedEvent.client_name}</span>
                </div>
              )}

              {selectedEvent.description && (
                <div className="pt-4 border-t border-[#E2E8F0]">
                  <p className="text-sm text-[#64748B]">{selectedEvent.description}</p>
                </div>
              )}

              {selectedEvent.appointment_type === 'visit' && selectedEvent.visit_id && (
                <div className="pt-4">
                  <Button 
                    className="w-full bg-[#176DFF] hover:bg-[#176DFF]/90"
                    onClick={() => {
                      navigate(`/operations/visits/${selectedEvent.visit_id}`);
                      setSelectedEvent(null);
                    }}
                  >
                    View Visit Details
                  </Button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
